/**
 * QuietDeviceStrip — collapsed footer listing roster devices with no alarms
 * under the current filter, so a healthy generator is visibly accounted for
 * rather than merely absent from the list.
 *
 * The label says "in this view", not "no active alarms", and that wording is
 * load-bearing: membership is filter-relative. Under the default
 * Needs-Attention filter a device whose only alarm is acknowledged has no rows
 * in `filtered` and therefore appears here — calling that device "no active
 * alarms" would tell an operator something false.
 *
 * This is the ONLY consumer of the config.site_devices roster on this page.
 * A failed roster never removes alarm rows — that isolation is the point
 * (#624). But it is not silent either: the strip is an accounting claim
 * ("these devices are quiet"), and an operator who sees no strip could
 * reasonably read it as "every device has an alarm" or simply not notice the
 * omission. So a failed roster says so, and offers a retry.
 */

import type { SiteDevice } from '@/types/alarm.types';

interface Props {
  devices: SiteDevice[];
  alarmingDeviceIds: Set<string>;
  /** Roster fetch failed — render the degraded notice instead of the list. */
  isError?: boolean;
  onRetry?: () => void;
}

export function QuietDeviceStrip({ devices, alarmingDeviceIds, isError, onRetry }: Props) {
  // Only claim "unavailable" when there is genuinely nothing to show. React
  // Query keeps the last good `data` when a *background refetch* fails, so
  // gating on isError alone would tear down a perfectly serviceable roster
  // and replace it with an error — a worse outcome than the slightly stale
  // list, given the alarm rows above are authoritative either way.
  if (isError && devices.length === 0) {
    return (
      <div
        data-testid="quiet-strip-error"
        className="flex items-center justify-between gap-3 border border-ems-border rounded bg-ems-bg-card px-3 py-2 text-xs text-ems-text-muted"
      >
        <span>
          Device roster unavailable — alarms above are complete, but quiet devices can’t be listed.
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="shrink-0 px-3 py-1 rounded border border-ems-border text-ems-text-secondary hover:text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-blue/40"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const quiet = devices.filter((d) => !alarmingDeviceIds.has(d.device_id));
  if (quiet.length === 0) return null;

  return (
    <details className="border border-ems-border rounded bg-ems-bg-card" data-testid="quiet-strip">
      <summary
        data-testid="quiet-strip-summary"
        className="cursor-pointer select-none px-3 py-2 text-[10px] uppercase tracking-wider text-ems-text-muted hover:text-ems-text-secondary"
      >
        No alarms in this view ({quiet.length} {quiet.length === 1 ? 'device' : 'devices'})
      </summary>
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5 px-3 pb-3 pt-1">
        {quiet.map((d) => (
          <li
            key={d.device_id}
            className="flex items-center gap-1.5 text-xs text-ems-text-secondary"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ems-green shrink-0" />
            {d.device_id}
          </li>
        ))}
      </ul>
    </details>
  );
}
